import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { ADMIN_DOMAIN } from '../utils/constants';
import { AuthState } from '../types';

const VIEW_AS_STORAGE_KEY = 'admin_view_as_company_id'
export const PASSWORD_RECOVERY_STORAGE_KEY = 'password_recovery_redirect'

function readPasswordRecoveryPending(): boolean {
  try {
    return sessionStorage.getItem(PASSWORD_RECOVERY_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writePasswordRecoveryPending(pending: boolean): void {
  try {
    if (pending) sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, '1')
    else sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

function urlIndicatesPasswordRecovery(): boolean {
  if (typeof window === 'undefined') return false
  const hash = window.location.hash || ''
  const search = window.location.search || ''
  return (
    hash.includes('type=recovery') ||
    search.includes('type=recovery') ||
    /[?&#]type=recovery\b/.test(`${search}${hash}`)
  )
}

function cleanAuthParamsFromUrl(): void {
  if (typeof window === 'undefined') return
  const hash = window.location.hash || ''
  const search = window.location.search || ''
  const hasAuthHash =
    hash.includes('access_token') ||
    hash.includes('type=email') ||
    hash.includes('type=recovery') ||
    hash.includes('refresh_token')
  const hasAuthQuery = search.includes('code=') || search.includes('type=recovery')
  if (hasAuthHash || hasAuthQuery) {
    window.history.replaceState(null, '', window.location.pathname)
  }
}

function isAbortLikeError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && /abort|signal/i.test(err.message)) return true;
  return false;
}

function readStoredViewAsCompanyId(): string | null {
  try {
    return sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredViewAsCompanyId(companyId: string | null): void {
  try {
    if (companyId) sessionStorage.setItem(VIEW_AS_STORAGE_KEY, companyId);
    else sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

interface AuthContextType extends AuthState {
  /** companyId used for branding/settings: view-as (admins) else membership company */
  effectiveCompanyId: string | null;
  /** True while forgot-password recovery must show set-new-password UI */
  passwordRecoveryPending: boolean;
  clearPasswordRecovery: () => void;
  setViewAsCompanyId: (companyId: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'azure') => Promise<void>;
  /** Tier B company SSO — domain from work email (e.g. acme.com). */
  signInWithSSO: (domainOrEmail: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, designation?: string | null, designation_other?: string | null) => Promise<void>;
  signOut: () => Promise<void>;
  isSignUp: boolean;
  setIsSignUp: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SIGNUP'; payload: boolean }
  | { type: 'SET_COMPANY'; payload: string | null }
  | { type: 'SET_VIEW_AS_COMPANY'; payload: string | null };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_USER': {
      if (!action.payload) {
        return {
          ...state,
          user: null,
          isAdmin: false,
          companyId: null,
          viewAsCompanyId: null,
          loading: false,
        };
      }
      const isAdmin = action.payload.email?.endsWith(`@${ADMIN_DOMAIN}`) || false;
      const sameUser = state.user?.id === action.payload.id;
      return {
        ...state,
        user: { id: action.payload.id, email: action.payload.email || '' },
        isAdmin,
        // Keep companyId across same-user session refresh; clear on user switch
        companyId: sameUser ? state.companyId : null,
        viewAsCompanyId: isAdmin ? (sameUser ? state.viewAsCompanyId : null) : null,
      };
    }
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_SIGNUP':
      return { ...state };
    case 'SET_COMPANY':
      return { ...state, companyId: action.payload };
    case 'SET_VIEW_AS_COMPANY':
      return { ...state, viewAsCompanyId: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  loading: true,
  isAdmin: false,
  companyId: null,
  viewAsCompanyId: null,
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const [isSignUp, setIsSignUp] = React.useState(false);
  const [passwordRecoveryPending, setPasswordRecoveryPending] = React.useState(() =>
    readPasswordRecoveryPending() || urlIndicatesPasswordRecovery()
  );

  const markPasswordRecoveryPending = React.useCallback(() => {
    writePasswordRecoveryPending(true);
    setPasswordRecoveryPending(true);
  }, []);

  const clearPasswordRecovery = React.useCallback(() => {
    writePasswordRecoveryPending(false);
    setPasswordRecoveryPending(false);
  }, []);

  // Load company when user is set; runs outside auth listener to avoid async-in-callback issues.
  // Always clears loading when done so ES guards never decide access before membership resolves.
  const loadUserCompany = React.useCallback(async (userId: string) => {
    try {
      const { data: userCompany, error } = await supabase
        .from('user_companies')
        .select('company_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.warn('Failed to load user company:', error.message);
        return;
      }
      if (userCompany?.company_id) {
        dispatch({ type: 'SET_COMPANY', payload: userCompany.company_id });
        return;
      }

      // Company SSO: copy membership from an email twin if present; else when the
      // company has sso_auto_provision, insert member for a verified SAML identity.
      const { data: ensuredCompanyId, error: ensureError } = await supabase.rpc(
        'ensure_company_membership_for_email'
      );
      if (ensureError) {
        console.warn('Failed to ensure company membership:', ensureError.message);
        return;
      }
      if (ensuredCompanyId) {
        dispatch({ type: 'SET_COMPANY', payload: ensuredCompanyId as string });
      }
    } catch (err) {
      // Silence expected abort errors (e.g. React StrictMode, tab switch, or Supabase client cleanup)
      if (isAbortLikeError(err)) return;
      console.error('Error loading user company:', err);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Single source of truth: only onAuthStateChange (INITIAL_SESSION, SIGNED_IN, etc.)
  // Do not call getSession() here - it races with the listener and can cause abort errors.
  useEffect(() => {
    // Mark recovery early from URL, but do NOT strip auth params until after the client detects the session
    if (urlIndicatesPasswordRecovery()) {
      markPasswordRecoveryPending();
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Keep callback synchronous per Supabase docs (no async/await, no other Supabase calls here)
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecoveryPending();
      }

      const prev = stateRef.current;
      const nextUser = session?.user ?? null;

      // Tab return often fires TOKEN_REFRESHED — avoid new user object / company reload churn
      if (event === 'TOKEN_REFRESHED' && nextUser && prev.user?.id === nextUser.id) {
        const nextEmail = nextUser.email || '';
        if (prev.user.email !== nextEmail) {
          dispatch({ type: 'SET_USER', payload: nextUser });
        }
        setTimeout(() => cleanAuthParamsFromUrl(), 0);
        if (prev.companyId || prev.isAdmin) {
          dispatch({ type: 'SET_LOADING', payload: false });
        } else {
          // Membership still unknown — keep loading until loadUserCompany finishes
          dispatch({ type: 'SET_LOADING', payload: true });
          const userId = nextUser.id;
          setTimeout(() => {
            loadUserCompany(userId);
          }, 0);
        }
        return;
      }

      dispatch({ type: 'SET_USER', payload: nextUser });

      if (!nextUser) {
        writeStoredViewAsCompanyId(null);
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      // Clean tokens from the address bar after the session exists
      if (
        event === 'PASSWORD_RECOVERY' ||
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'INITIAL_SESSION'
      ) {
        setTimeout(() => cleanAuthParamsFromUrl(), 0);
      }

      const email = nextUser.email || '';
      const isAdmin = email.endsWith(`@${ADMIN_DOMAIN}`);
      if (isAdmin) {
        const stored = readStoredViewAsCompanyId();
        if (stored) dispatch({ type: 'SET_VIEW_AS_COMPANY', payload: stored });
        // Admins do not need companyId for Early Signals gate
        dispatch({ type: 'SET_LOADING', payload: false });
      } else {
        writeStoredViewAsCompanyId(null);
        // Keep loading true until user_companies (+ ensure RPC) resolves — prevents
        // premature ES deny → /?view=landing sticky redirect for SSO tenants.
        dispatch({ type: 'SET_LOADING', payload: true });
      }

      // Schedule company load after callback returns to avoid deadlock/abort issues
      const userId = nextUser.id;
      setTimeout(() => {
        loadUserCompany(userId);
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [loadUserCompany, markPasswordRecoveryPending]);

  const setViewAsCompanyId = React.useCallback(
    (companyId: string | null) => {
      if (!state.isAdmin) return;
      writeStoredViewAsCompanyId(companyId);
      dispatch({ type: 'SET_VIEW_AS_COMPANY', payload: companyId });
    },
    [state.isAdmin]
  );

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  /** Additive SSO — password login remains. Providers must be enabled in Supabase Auth. */
  const signInWithOAuth = async (provider: 'google' | 'azure') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
        ...(provider === 'azure' ? { scopes: 'openid email profile' } : {}),
      },
    });
    if (error) throw error;
  };

  /** Company SSO (SAML) — pass work email or bare domain registered in Enable SSO. */
  const signInWithSSO = async (domainOrEmail: string) => {
    const raw = domainOrEmail.trim().toLowerCase();
    const domain = raw.includes('@') ? raw.split('@')[1] : raw.replace(/^@/, '');
    if (!domain) throw new Error('Enter your work email (for example you@acme.com).');

    const { data: companyRows } = await supabase
      .from('companies')
      .select('preferred_sign_in, sso_provider_id')
      .eq('status', 'active')
      .ilike('domain', domain)
      .limit(1);
    const company = companyRows?.[0] as
      | { preferred_sign_in: string | null; sso_provider_id: string | null }
      | undefined;
    const preferred = company?.preferred_sign_in;
    const hasSaml = Boolean(company?.sso_provider_id);

    if (preferred === 'microsoft') {
      throw new Error(
        'Your company signs in with Microsoft. Use the Microsoft button above — Company SSO is only for SAML (Okta / company IdP).'
      );
    }
    if (preferred === 'google') {
      throw new Error(
        'Your company signs in with Google. Use the Google button above — Company SSO is only for SAML (Okta / company IdP).'
      );
    }
    if (company && !hasSaml) {
      throw new Error(
        'Company SSO is not set up for your domain. If your company uses Microsoft or Google, use that button above.'
      );
    }

    const { data, error } = await supabase.auth.signInWithSSO({
      domain,
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) {
      const msg = error.message || '';
      if (/no sso provider|sso_provider_not_found/i.test(msg)) {
        throw new Error(
          'No Company SSO (SAML) for this domain. If your company uses Microsoft or Google, use that button above — do not use Company SSO.'
        );
      }
      throw error;
    }
    if (data?.url) {
      window.location.assign(data.url);
    }
  };

  const signUp = async (email: string, _password: string, name: string, designation?: string | null, designation_other?: string | null) => {
    // password parameter kept for interface compatibility but not used in manual signup flow
    // Extract company domain from email
    const companyDomain = email.split('@')[1]
    if (!companyDomain) {
      throw new Error('Invalid email address')
    }

    // Create pending_registrations record directly (don't create auth user yet)
    // Admin will manually create the account after reviewing the email request
    const { error } = await supabase
      .from('pending_registrations')
      .insert({
        email: email,
        name: name,
        company_domain: companyDomain,
        status: 'pending',
        designation: designation ?? null,
        designation_other: designation_other ?? null
      })
    
    if (error) {
      // If duplicate, that's okay - user may have tried multiple times
      if (error.code !== '23505') {
        throw error
      }
    }

    // Return success - AuthModal will handle opening email client
  };

  const signOut = async () => {
    writeStoredViewAsCompanyId(null);
    dispatch({ type: 'SET_VIEW_AS_COMPANY', payload: null });
    clearPasswordRecovery();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    // Explicitly reset state as fallback if onAuthStateChange doesn't fire immediately
    dispatch({ type: 'SET_USER', payload: null });
  };

  const effectiveCompanyId = state.isAdmin
    ? state.viewAsCompanyId ?? state.companyId
    : state.companyId;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        effectiveCompanyId,
        passwordRecoveryPending,
        clearPasswordRecovery,
        setViewAsCompanyId,
        signIn,
        signInWithOAuth,
        signInWithSSO,
        signUp,
        signOut,
        isSignUp,
        setIsSignUp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
